import React, { useState, useEffect } from 'react';
import { X, Paperclip, FileText, AlertTriangle } from 'lucide-react';
import { DEFAULT_TIME_ZONE, timeZoneLabel, todayInZone } from '../lib/workerTime';

// "Hoy" en la zona del trabajador (antes era el día UTC del navegador).
const todayStr = (tz) => todayInZone(tz || DEFAULT_TIME_ZONE);

// `range`  → fecha desde + fecha hasta (vacaciones, bajas)
// `hours`  → fecha única + hora desde + hora hasta (ausencia justificada por horas)
// si ambos son false → solo una fecha
const TYPE_CONFIG = {
  vacaciones:   { label: 'Solicitar vacaciones',          range: true,  hours: false, placeholder: 'Motivo (opcional)',                 requiresFile: false },
  baja:         { label: 'Informar día completo',           range: true,  hours: false, placeholder: 'Motivo (médico, boda, funeral, asuntos propios…)', requiresFile: false },
  ausencia:     { label: 'Solicitar por horas',           range: false, hours: true,  placeholder: 'Motivo (médico, funeral, etc.)',    requiresFile: false },
  permiso:      { label: 'Solicitar permiso',             range: true,  hours: false, placeholder: 'Motivo',                            requiresFile: false },
  horaExtra:    { label: 'Registrar hora extra',          range: false, hours: false, placeholder: 'Justificación',                     requiresFile: false },
  ajuste:       { label: 'Ajustar fichaje',               range: false, hours: false, placeholder: 'Motivo del ajuste',                 requiresFile: false },
};

const ACCEPT = 'image/*,application/pdf';
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// `timeZone` (opcional): zona del trabajador que solicita. Solo se usa para
// avisar de que las horas son "Hora de Canarias" (el padre convierte
// fecha+hora a instante con zonedDateTimeToInstant en esa zona).
const RequestModal = ({ open, tipo, initialRange, onClose, onSubmit, timeZone = null }) => {
  const cfg = tipo ? TYPE_CONFIG[tipo] : null;
  const tzHint = timeZoneLabel(timeZone);
  const [fechaInicio, setFechaInicio] = useState(() => todayStr(timeZone));
  const [fechaFin, setFechaFin] = useState(() => todayStr(timeZone));
  const [horaInicio, setHoraInicio] = useState('11:00');
  const [horaFin, setHoraFin] = useState('12:00');
  const [motivo, setMotivo] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [hourError, setHourError] = useState(null);

  useEffect(() => {
    if (open) {
      setFechaInicio(initialRange?.start || todayStr(timeZone));
      setFechaFin(initialRange?.end || initialRange?.start || todayStr(timeZone));
      setHoraInicio('11:00');
      setHoraFin('12:00');
      setMotivo('');
      setFile(null);
      setFileError(null);
      setHourError(null);
    }
  }, [open, tipo, initialRange, timeZone]);

  if (!open || !cfg) return null;

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFileError(null);
    if (!f) { setFile(null); return; }
    if (f.size > MAX_BYTES) {
      setFileError('El archivo supera 10 MB.');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (cfg.requiresFile && !file) {
      setFileError('Adjunta el justificante de la ausencia (PDF o imagen).');
      return;
    }
    if (cfg.hours) {
      // Validamos que hora fin > hora inicio en el mismo día.
      if (!horaInicio || !horaFin || horaFin <= horaInicio) {
        setHourError('La hora de fin debe ser posterior a la de inicio.');
        return;
      }
    }
    onSubmit({
      id: `req-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      tipo,
      fechaInicio,
      fechaFin: cfg.range ? fechaFin : fechaInicio,
      horaInicio: cfg.hours ? horaInicio : null,
      horaFin: cfg.hours ? horaFin : null,
      motivo: motivo.trim() || null,
      file,
      estado: 'pendiente',
      createdAt: new Date().toISOString(),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-display text-lg font-bold text-ink">{cfg.label}</h2>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className={cfg.range ? 'grid grid-cols-2 gap-3' : ''}>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
                {cfg.range ? 'Desde' : 'Fecha'}
              </label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                required
              />
            </div>
            {cfg.range && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Hasta</label>
                <input
                  type="date"
                  value={fechaFin}
                  min={fechaInicio}
                  onChange={(e) => setFechaFin(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                  required
                />
              </div>
            )}
          </div>

          {cfg.hours && (
            <div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Hora desde</label>
                  <input
                    type="time"
                    value={horaInicio}
                    onChange={(e) => { setHoraInicio(e.target.value); setHourError(null); }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Hora hasta</label>
                  <input
                    type="time"
                    value={horaFin}
                    onChange={(e) => { setHoraFin(e.target.value); setHourError(null); }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                    required
                  />
                </div>
              </div>
              {tzHint && <div className="text-[11px] text-ink-muted mt-1">{tzHint}</div>}
              {hourError && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-danger">
                  <AlertTriangle size={11} /> {hourError}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">{cfg.placeholder}</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm resize-none"
            />
          </div>

          {(tipo === 'baja' || tipo === 'vacaciones' || tipo === 'ausencia') && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2 inline-flex items-center gap-1.5">
                <Paperclip size={11} />
                {cfg.requiresFile ? 'Justificante (obligatorio)' : 'Adjuntar documento (opcional)'}
              </label>
              <label className="flex items-center gap-3 px-3 py-2.5 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary-soft/30 transition-colors">
                <input
                  type="file"
                  accept={ACCEPT}
                  onChange={onFileChange}
                  className="hidden"
                />
                <FileText size={16} className={file ? 'text-primary-darker' : 'text-ink-muted'} />
                <div className="flex-1 min-w-0 text-sm">
                  {file ? (
                    <>
                      <div className="font-semibold text-ink truncate">{file.name}</div>
                      <div className="text-xs text-ink-muted">{(file.size / 1024).toFixed(0)} KB · {file.type || 'archivo'}</div>
                    </>
                  ) : (
                    <span className="text-ink-muted">Seleccionar PDF o imagen…</span>
                  )}
                </div>
              </label>
              {fileError && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-danger">
                  <AlertTriangle size={11} /> {fileError}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors"
            >
              Enviar solicitud
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestModal;
